import { useEffect, lazy, Suspense } from 'react'
import { Route, Routes, BrowserRouter, useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'

import AppTitle from './app-title'
import config from './config'
import { AuthProvider } from './security'
import { TelemetryProvider } from './telemetry'
import withAppContext from './with-app-context'
import { withAITracking } from '@microsoft/applicationinsights-react-js'
import { ai } from './telemetry/telemetry-service'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PrivateRoute } from './security/private-route'
import HealthCheck from './components/health-check/health-check'
import { useAppContext } from './app-context'
import { showNewVersionAlert, GoogleMapsProvider } from './components'
import NavigationMenu from './components/navigation-menu/navigation-menu'
import SafetyCase from './features/safety-case/safety-case'

import './app.scss'
import './app-layout.scss'
import './css/bootstrap.min.scss'
import './css/style.bundle.scss'

const Inspections = lazy(() => import('./features/inspections/inspections'))
const Dashboard = lazy(() => import('./features/dashboard/dashboard'))
const Admin = lazy(() => import('./features/admin/admin'))
const SuperAdmin = lazy(() => import('./features/super-admin/super-admin'))

const Spinner = lazy(() => import('./components/spinner/spinner'))
const Footer = lazy(() => import('./components').then(module => ({ default: module.Footer })))
const Alert = lazy(() => import('./components/alert/alert'))
const Error = lazy(() => import('./features/error'))
const Search = lazy(() => import('./features/search/search'))
const FAQ = lazy(() => import('./features/faq/faq'))
const EditInspection = lazy(() => import('./features/inspections/edit-inspection'))
const AddDiscrepancy = lazy(() => import('./features/discrepancies/add-edit-discrepancy/add-discrepancy-page'))
const EditDiscrepancy = lazy(() => import('./features/discrepancies/add-edit-discrepancy/edit-discrepancy-page'))
const Journal = lazy(() => import('./features/journal/journal'))
const Profile = lazy(() => import('./features/profile/profile'))
const Reports = lazy(() => import('./features/reports/reports-page'))
const WorkOrdersLanding = lazy(() => import('./features/work-orders/work-orders-landing'))
const CheckLists = lazy(() => import('./features/checklists/checklists-page'))
const ChecklistPage = lazy(() => import('./features/checklists/checklist-page'))
const Training = lazy(() => import('./features/training/training-routes'))
const ReleaseNotes = lazy(() => import('./features/release-notes/release-notes'))
const NotFound = lazy(() => import('./features/not-found'))
const SafetyReport = lazy(() => import('./features/safety-report/safety-report'))
const SafetyReportStatus = lazy(() => import('./features/safety-report/safety-report-status'))

const queryClient = new QueryClient()

const baseTitle = 'App 139 -'

const baseSafetyCasePath = '/safety-cases'

const AppContent = () => {
  const location = useLocation()

  const match = location.pathname.match(/^\/safety-report\/([^/]+)(?:\/status\/([^/]+))?$/)
  const portalKey = match ? match[1] : undefined
  const trackingKey = match ? match[2] : undefined
  const isSafetyReport = !!portalKey && !trackingKey
  const isSafetyReportStatus = !!portalKey && !!trackingKey

  if (isSafetyReport || isSafetyReportStatus) {
    return (
      <div className="app-139-page">
        <main className="app-139-main">
          {isSafetyReport && (
            <>
              <GoogleMapsProvider
                apiKey={config.googleMaps.apiKey}
                version={config.googleMaps.version}
                libraries={config.googleMaps.libraries}
              >
                <SafetyReport portalKey={portalKey} />
              </GoogleMapsProvider>
              <div className="alert-overlay-container">
                <div className="alert-overlay">
                  <Alert timeoutInMilliseconds={config.alert.timeoutInMilliseconds} />
                </div>
              </div>
            </>
          )}

          {isSafetyReportStatus && <SafetyReportStatus portalKey={portalKey} trackingKey={trackingKey} />}
        </main>

        <footer className="app-139-footer px-3 py-0 w-100">
          <Footer />
        </footer>
      </div>
    )
  }

  return (
    <AuthProvider>
      <div className="app-139-page">
        <header className="w-100">
          <NavigationMenu />
        </header>

        <Suspense
          fallback={
            <div className="vh-100">
              <Spinner />
            </div>
          }
        >
          <main className="app-139-main">
            <Routes>
              <Route
                path="/healthcheck"
                element={
                  <AppTitle title={`${baseTitle} Health Check`}>
                    <HealthCheck />
                  </AppTitle>
                }
              />

              <Route path="/signin-oidc" element={<div />} />

              <Route
                path="/safety-report"
                element={
                  <AppTitle title={`${baseTitle} Report Safety Concern`}>
                    <SafetyReport />
                  </AppTitle>
                }
              />

              <Route element={<PrivateRoute />}>
                <Route
                  path="/"
                  element={
                    <AppTitle title={`${baseTitle} Dashboard`}>
                      <Dashboard />
                    </AppTitle>
                  }
                />

                <Route
                  path={`${baseSafetyCasePath}/*`}
                  element={<SafetyCase baseTitle={baseTitle} basePath={baseSafetyCasePath} />}
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Admin`}>
                      <Admin />
                    </AppTitle>
                  }
                  path="admin/*"
                />
                <Route
                  element={
                    <AppTitle title={`${baseTitle} Hero`}>
                      <SuperAdmin />
                    </AppTitle>
                  }
                  path="hero/*"
                />
                <Route
                  element={
                    <AppTitle title={`${baseTitle} FAQ`}>
                      <FAQ />
                    </AppTitle>
                  }
                  path="faq"
                />
                <Route path="inspections/*">
                  <Route
                    element={
                      <AppTitle title={`${baseTitle} Inspections - List`}>
                        <Inspections />
                      </AppTitle>
                    }
                    index
                  />
                  <Route
                    element={
                      <AppTitle title={`${baseTitle} Inspection - Edit`}>
                        <EditInspection />
                      </AppTitle>
                    }
                    path=":inspectionId"
                  />
                  <Route
                    element={
                      <AppTitle title={`${baseTitle} Inspection - Add Discrepancy`}>
                        <AddDiscrepancy />
                      </AppTitle>
                    }
                    path=":inspectionId/discrepancy"
                  />
                  <Route
                    element={
                      <AppTitle title={`${baseTitle} Inspection - Edit Disrepancy`}>
                        <EditDiscrepancy />
                      </AppTitle>
                    }
                    path=":inspectionId/discrepancy/:discrepancyId"
                  />
                </Route>
                <Route
                  element={
                    <AppTitle title={`${baseTitle} Journal`}>
                      <Journal />
                    </AppTitle>
                  }
                  path="/journal"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Training`}>
                      <Training />
                    </AppTitle>
                  }
                  path="/training/*"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Profile`}>
                      <Profile />
                    </AppTitle>
                  }
                  path="profile/*"
                />
                <Route
                  path="reports/*"
                  element={
                    <AppTitle title={`${baseTitle} Reports`}>
                      <Reports />
                    </AppTitle>
                  }
                />
                <Route
                  element={
                    <AppTitle title={`${baseTitle} Search`}>
                      <Search />
                    </AppTitle>
                  }
                  path="search/*"
                />
                <Route
                  element={
                    <AppTitle title={`${baseTitle} Work Orders - List`}>
                      <WorkOrdersLanding />
                    </AppTitle>
                  }
                  path="work-orders/*"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Checklists`}>
                      <CheckLists />
                    </AppTitle>
                  }
                  path="checklists"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Checklists`}>
                      <ChecklistPage />
                    </AppTitle>
                  }
                  path="checklists/:checklistId"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Release Notes`}>
                      <ReleaseNotes />
                    </AppTitle>
                  }
                  path="release-notes/*"
                />

                <Route
                  element={
                    <AppTitle title={`${baseTitle} Not Found`}>
                      <NotFound />
                    </AppTitle>
                  }
                  path="*"
                />
              </Route>
            </Routes>
          </main>
        </Suspense>

        <footer className="app-139-footer px-3 py-0 w-100">
          <Footer />
        </footer>

        <div className="alert-overlay-container">
          <div className="alert-overlay">
            <Alert timeoutInMilliseconds={config.alert.timeoutInMilliseconds} />
          </div>
        </div>
      </div>
    </AuthProvider>
  )
}

const App = () => {
  const { dispatch: appDispatch } = useAppContext()

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const scripts = document.getElementsByTagName('script')
        const bundlePathPrefix = '/static/js/'
        const mainBundlePrefix = 'main.'
        if (scripts?.length > 0) {
          for (let i = 0; i < scripts.length; i++) {
            const script = scripts[i]
            if (script.src.includes(`${bundlePathPrefix}${mainBundlePrefix}`)) {
              const index = script.src.indexOf(bundlePathPrefix)
              const currentBundle = script.src.substring(index + bundlePathPrefix.length)
              fetch(`/index.html?timestamp=${Date.now()}`)
                .then(response => response.text())
                .then(response => {
                  const bundleUpdated =
                    response.includes(`${bundlePathPrefix}${mainBundlePrefix}`) && !response.includes(currentBundle)
                  if (bundleUpdated) {
                    appDispatch(showNewVersionAlert())
                  }
                })
              break
            }
          }
        }
      } catch {
        console.error('Something went wrong while checking for new version.')
      }
    }, 60000)
    return () => clearInterval(interval)
  }, [appDispatch])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary FallbackComponent={Error}>
          <TelemetryProvider config={config.appInsights}>
            <AppContent />
          </TelemetryProvider>
        </ErrorBoundary>
      </BrowserRouter>

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default withAITracking(ai.reactPlugin, withAppContext(App), 'App', 'app-139-page')
